import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import './Adiantamento.css'

// Constantes de roles
const USER_ROLES = {
  ADMINISTRADOR: 'administrador',
  INSTALADOR: 'instalador'
}

const Adiantamento = ({ userRole, userEmpresa }) => {
  const [adiantamentos, setAdiantamentos] = useState([])
  const [colaboradores, setColaboradores] = useState([])
  const [pgtoTipos, setPgtoTipos] = useState([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [itemToDelete, setItemToDelete] = useState(null)
  const [formData, setFormData] = useState({
    colaborador_id: '',
    data_lancamento: new Date().toISOString().split('T')[0],
    valor_pago: '',
    descricao: '',
    pgto_id: ''
  })
  const [filtros, setFiltros] = useState({
    colaborador_ids: [], // Array de IDs de colaboradores selecionados
    data_inicio: '',
    data_fim: '',
    pgto_id: ''
  })
  
  // Estados para o searchbox de colaboradores
  const [colaboradorSearch, setColaboradorSearch] = useState('')
  const [showColaboradorDropdown, setShowColaboradorDropdown] = useState(false)
  const [selectedColaboradores, setSelectedColaboradores] = useState([])

  // Função para mostrar notificações
  const showNotification = (message, type = 'info') => {
    const id = Date.now()
    setNotifications(prev => [...prev, { id, message, type }])
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id))
    }, 5000)
  }

  // Carregar dados iniciais
  useEffect(() => {
    loadAdiantamentos()
    loadColaboradores()
    loadPgtoTipos()
  }, [userEmpresa])
  
  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.colaborador-searchbox')) {
        setShowColaboradorDropdown(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Carregar adiantamentos
  const loadAdiantamentos = async () => {
    try {
      setLoading(true)
      console.log('🔍 Carregando adiantamentos...', { userRole, userEmpresa: userEmpresa?.id })

      // Se for administrador, carregar todos os adiantamentos
      if (userRole === 'administrador') {
        console.log('👑 Usuário é administrador - carregando todos os adiantamentos')
        
        let query = supabase
          .from('adiantamentos_colaboradores')
          .select(`
            *,
            colaboradores (
              id,
              nome,
              empresa_id
            ),
            pgto_tipos (
              id,
              descricao
            )
          `)
          .order('data_lancamento', { ascending: false })

        // Aplicar filtros específicos
        if (filtros.colaborador_ids && filtros.colaborador_ids.length > 0) {
          query = query.in('colaborador_id', filtros.colaborador_ids)
        }
        
        if (filtros.data_inicio) {
          query = query.gte('data_lancamento', filtros.data_inicio)
        }
        
        if (filtros.data_fim) {
          query = query.lte('data_lancamento', filtros.data_fim)
        }

        if (filtros.pgto_id) {
          query = query.eq('pgto_id', filtros.pgto_id)
        }

        const { data, error } = await query

        if (error) {
          console.error('❌ Erro ao carregar adiantamentos:', error)
          showNotification('Erro ao carregar adiantamentos', 'error')
          return
        }

        setAdiantamentos(data || [])
        console.log('✅ Adiantamentos carregados (admin):', data?.length || 0)
        return
      }

      // Para usuários não-administradores, filtrar por empresa
      if (!userEmpresa?.id) {
        console.log('❌ Usuário não tem empresa definida')
        setAdiantamentos([])
        return
      }

      console.log('🏢 Filtrando adiantamentos por empresa:', userEmpresa.id)

      // Buscar colaboradores da empresa
      const { data: colaboradoresEmpresa, error: errorColaboradores } = await supabase
        .from('colaboradores')
        .select('id')
        .eq('empresa_id', userEmpresa.id)
        .eq('ativo', true)

      if (errorColaboradores) {
        console.error('❌ Erro ao carregar colaboradores da empresa:', errorColaboradores)
        setAdiantamentos([])
        return
      }

      const colaboradoresIds = colaboradoresEmpresa?.map(c => c.id) || []
      console.log('👥 Colaboradores da empresa encontrados:', colaboradoresIds.length, colaboradoresIds)

      if (colaboradoresIds.length === 0) {
        console.log('⚠️ Nenhum colaborador encontrado para a empresa')
        setAdiantamentos([])
        return
      }

      // Buscar adiantamentos que tenham colaboradores da empresa
      let query = supabase
        .from('adiantamentos_colaboradores')
        .select(`
          *,
          colaboradores (
            id,
            nome,
            empresa_id
          ),
          pgto_tipos (
            id,
            descricao
          )
        `)
        .in('colaborador_id', colaboradoresIds)
        .order('data_lancamento', { ascending: false })

      // Aplicar filtros específicos
      if (filtros.colaborador_ids && filtros.colaborador_ids.length > 0) {
        // Intersectar com colaboradores selecionados nos filtros
        const colaboradoresFiltrados = colaboradoresIds.filter(id => 
          filtros.colaborador_ids.includes(id)
        )
        if (colaboradoresFiltrados.length > 0) {
          query = query.in('colaborador_id', colaboradoresFiltrados)
        } else {
          // Se nenhum colaborador selecionado pertence à empresa, não mostrar nada
          setAdiantamentos([])
          return
        }
      }
      
      if (filtros.data_inicio) {
        query = query.gte('data_lancamento', filtros.data_inicio)
      }
      
      if (filtros.data_fim) {
        query = query.lte('data_lancamento', filtros.data_fim)
      }

      if (filtros.pgto_id) {
        query = query.eq('pgto_id', filtros.pgto_id)
      }

      const { data, error } = await query

      if (error) {
        console.error('❌ Erro ao carregar adiantamentos:', error)
        showNotification('Erro ao carregar adiantamentos', 'error')
        return
      }

      setAdiantamentos(data || [])
      console.log('✅ Adiantamentos carregados (filtrados):', data?.length || 0)

    } catch (error) {
      console.error('❌ Erro ao carregar adiantamentos:', error)
      showNotification('Erro ao carregar adiantamentos', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Carregar colaboradores
  const loadColaboradores = async () => {
    try {
      let query = supabase
        .from('colaboradores')
        .select('id, nome, empresa_id')
        .eq('ativo', true)
        .order('nome')

      // Filtrar por empresa se o usuário não for administrador
      if (userRole !== 'administrador' && userEmpresa?.id) {
        query = query.eq('empresa_id', userEmpresa.id)
      }

      const { data, error } = await query

      if (error) {
        console.error('Erro ao carregar colaboradores:', error)
        return
      }

      setColaboradores(data || [])
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error)
    }
  }

  // Carregar tipos de pagamento
  const loadPgtoTipos = async () => {
    try {
      const { data, error } = await supabase
        .from('pgto_tipos')
        .select('id, descricao')
        .order('id')

      if (error) {
        console.error('Erro ao carregar tipos de pagamento:', error)
        return
      }

      setPgtoTipos(data || [])
    } catch (error) {
      console.error('Erro ao carregar tipos de pagamento:', error)
    }
  }

  // Verificar se o tipo selecionado é "Pagamento"
  const isTipoPagamento = () => {
    const tipoSelecionado = pgtoTipos.find(tipo => tipo.id.toString() === formData.pgto_id)
    return tipoSelecionado?.descricao === 'Pagamento'
  }

  // Verificar se o usuário pode editar (administrador ou administrativo)
  const podeEditar = () => {
    return userRole === 'administrador' || userRole === 'administrativo'
  }

  // Verificar se o usuário pode excluir (apenas administrador)
  const podeExcluir = () => {
    return userRole === 'administrador'
  }

  // Gerar descrição baseada na data selecionada
  const gerarDescricaoPagamento = (data) => {
    if (!data) return ''
    const dataObj = new Date(data)
    const mesFormatado = dataObj.toLocaleDateString('pt-BR', { 
      year: 'numeric', 
      month: 'long' 
    })
    return `Pagamento referente ao ${mesFormatado}`
  }

  // Salvar adiantamento
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validação básica
    if (!formData.colaborador_id || !formData.valor_pago || !formData.pgto_id) {
      showNotification('Preencha todos os campos obrigatórios', 'error')
      return
    }

    // Validação de descrição para tipos que não sejam Pagamento
    if (!isTipoPagamento() && !formData.descricao) {
      showNotification('Preencha a descrição', 'error')
      return
    }

    try {
      setLoading(true)

      // Gerar descrição baseada no tipo
      let descricao = formData.descricao
      if (isTipoPagamento()) {
        descricao = gerarDescricaoPagamento(formData.data_lancamento)
      }

      const adiantamentoData = {
        colaborador_id: formData.colaborador_id,
        data_lancamento: formData.data_lancamento,
        valor_pago: parseFloat(formData.valor_pago),
        descricao: descricao,
        pgto_id: parseInt(formData.pgto_id)
      }

      if (editingId) {
        // Atualizar
        const { error } = await supabase
          .from('adiantamentos_colaboradores')
          .update(adiantamentoData)
          .eq('id', editingId)

        if (error) {
          console.error('Erro ao atualizar adiantamento:', error)
          showNotification('Erro ao atualizar adiantamento', 'error')
          return
        }

        showNotification('Adiantamento atualizado com sucesso!', 'success')
      } else {
        // Criar
        const { error } = await supabase
          .from('adiantamentos_colaboradores')
          .insert(adiantamentoData)

        if (error) {
          console.error('Erro ao criar adiantamento:', error)
          showNotification('Erro ao criar adiantamento', 'error')
          return
        }

        showNotification('Adiantamento criado com sucesso!', 'success')
      }

      // Limpar formulário e recarregar dados
      setFormData({
        colaborador_id: '',
        data_lancamento: new Date().toISOString().split('T')[0],
        valor_pago: '',
        descricao: '',
        pgto_id: ''
      })
      setShowForm(false)
      setEditingId(null)
      loadAdiantamentos()
    } catch (error) {
      console.error('Erro ao salvar adiantamento:', error)
      showNotification('Erro ao salvar adiantamento', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Editar adiantamento
  const handleEdit = (adiantamento) => {
    setFormData({
      colaborador_id: adiantamento.colaborador_id,
      data_lancamento: adiantamento.data_lancamento,
      valor_pago: adiantamento.valor_pago.toString(),
      descricao: adiantamento.descricao,
      pgto_id: adiantamento.pgto_id ? adiantamento.pgto_id.toString() : ''
    })
    setEditingId(adiantamento.id)
    setShowForm(true)
  }

  // Abrir modal de confirmação de exclusão
  const handleDeleteClick = (id) => {
    setItemToDelete(id)
    setShowDeleteModal(true)
  }

  // Excluir adiantamento
  const handleDelete = async () => {
    if (!itemToDelete) return

    try {
      setLoading(true)

      const { error } = await supabase
        .from('adiantamentos_colaboradores')
        .delete()
        .eq('id', itemToDelete)

      if (error) {
        console.error('Erro ao excluir lançamento:', error)
        showNotification('Erro ao excluir lançamento', 'error')
        return
      }

      showNotification('Lançamento excluído com sucesso!', 'success')
      loadAdiantamentos()
      setShowDeleteModal(false)
      setItemToDelete(null)
    } catch (error) {
      console.error('Erro ao excluir lançamento:', error)
      showNotification('Erro ao excluir lançamento', 'error')
    } finally {
      setLoading(false)
    }
  }

  // Cancelar exclusão
  const handleCancelDelete = () => {
    setShowDeleteModal(false)
    setItemToDelete(null)
  }

  // Aplicar filtros
  const handleFilter = () => {
    loadAdiantamentos()
  }

  // Limpar filtros
  const clearFilters = () => {
    setFiltros({
      colaborador_ids: [],
      data_inicio: '',
      data_fim: '',
      pgto_id: ''
    })
    setSelectedColaboradores([])
    setColaboradorSearch('')
    loadAdiantamentos()
  }
  
  // Funções para o searchbox de colaboradores
  const filteredColaboradores = colaboradores.filter(colaborador =>
    colaborador.nome.toLowerCase().includes(colaboradorSearch.toLowerCase())
  )
  
  const handleColaboradorSelect = (colaborador) => {
    const isSelected = selectedColaboradores.some(sel => sel.id === colaborador.id)
    
    if (isSelected) {
      // Remover da seleção
      const newSelected = selectedColaboradores.filter(sel => sel.id !== colaborador.id)
      setSelectedColaboradores(newSelected)
      setFiltros(prev => ({
        ...prev,
        colaborador_ids: newSelected.map(col => col.id)
      }))
    } else {
      // Adicionar à seleção
      const newSelected = [...selectedColaboradores, colaborador]
      setSelectedColaboradores(newSelected)
      setFiltros(prev => ({
        ...prev,
        colaborador_ids: newSelected.map(col => col.id)
      }))
    }
  }
  
  const handleRemoveColaborador = (colaboradorId) => {
    const newSelected = selectedColaboradores.filter(sel => sel.id !== colaboradorId)
    setSelectedColaboradores(newSelected)
    setFiltros(prev => ({
      ...prev,
      colaborador_ids: newSelected.map(col => col.id)
    }))
  }
  
  const clearColaboradorSearch = () => {
    setColaboradorSearch('')
    setShowColaboradorDropdown(false)
  }

  // Calcular total
  const calcularTotal = () => {
    return adiantamentos.reduce((total, adiantamento) => total + parseFloat(adiantamento.valor_pago), 0)
  }

  // Formatar moeda
  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor)
  }

  // Formatar data
  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR')
  }

  return (
    <div className="adiantamento-container">
      <div className="adiantamento-header">
        <h2>Lançamentos</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          Novo Lançamento
        </button>
      </div>

      {/* Filtros */}
      <div className="filtros-container">
        <div className="filtros-row">
          <div className="filtro-group colaborador-searchbox">
            <label>Colaborador(es):</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="searchbox-input"
                placeholder="Digite para pesquisar colaboradores..."
                value={colaboradorSearch}
                onChange={(e) => {
                  setColaboradorSearch(e.target.value)
                  setShowColaboradorDropdown(true)
                }}
                onFocus={() => setShowColaboradorDropdown(true)}
              />
              {colaboradorSearch && (
                <button
                  type="button"
                  className="searchbox-clear"
                  onClick={clearColaboradorSearch}
                >
                  ×
                </button>
              )}
              
              {showColaboradorDropdown && (
                <div className="searchbox-dropdown">
                  {filteredColaboradores.length === 0 ? (
                    <div className="searchbox-option">
                      Nenhum colaborador encontrado
                    </div>
                  ) : (
                    filteredColaboradores.map(colaborador => {
                      const isSelected = selectedColaboradores.some(sel => sel.id === colaborador.id)
                      return (
                        <div
                          key={colaborador.id}
                          className={`searchbox-option ${isSelected ? 'selected' : ''}`}
                          onClick={() => handleColaboradorSelect(colaborador)}
                        >
                          <input
                            type="checkbox"
                            className="searchbox-checkbox"
                            checked={isSelected}
                            onChange={() => {}} // Controlado pelo onClick
                          />
                          {colaborador.nome}
                        </div>
                      )
                    })
                  )}
                </div>
              )}
            </div>
            
            {selectedColaboradores.length > 0 && (
              <div className="searchbox-selected">
                {selectedColaboradores.map(colaborador => (
                  <span key={colaborador.id} className="selected-tag">
                    {colaborador.nome}
                    <button
                      type="button"
                      className="selected-tag-remove"
                      onClick={() => handleRemoveColaborador(colaborador.id)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="filtro-group">
            <label>Data Início:</label>
            <input
              type="date"
              value={filtros.data_inicio}
              onChange={(e) => setFiltros({...filtros, data_inicio: e.target.value})}
            />
          </div>

          <div className="filtro-group">
            <label>Data Fim:</label>
            <input
              type="date"
              value={filtros.data_fim}
              onChange={(e) => setFiltros({...filtros, data_fim: e.target.value})}
            />
          </div>

          <div className="filtro-group">
            <label>Tipo:</label>
            <select
              value={filtros.pgto_id}
              onChange={(e) => setFiltros({...filtros, pgto_id: e.target.value})}
            >
              <option value="">Todos</option>
              {pgtoTipos.map(tipo => (
                <option key={tipo.id} value={tipo.id}>
                  {tipo.descricao}
                </option>
              ))}
            </select>
          </div>

          <div className="filtro-actions">
            <button className="btn btn-secondary" onClick={handleFilter}>
              Filtrar
            </button>
            <button className="btn btn-outline" onClick={clearFilters}>
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Resumo */}
      <div className="resumo-container">
        <div className="resumo-item">
          <span className="resumo-label">Total de Lançamentos:</span>
          <span className="resumo-value">{adiantamentos.length}</span>
        </div>
        <div className="resumo-item">
          <span className="resumo-label">Valor Total:</span>
          <span className="resumo-value">{formatarMoeda(calcularTotal())}</span>
        </div>
      </div>

      {/* Lista de Lançamentos */}
      <div className="adiantamentos-list">
        {loading ? (
          <div className="loading">Carregando...</div>
        ) : adiantamentos.length === 0 ? (
          <div className="no-data">Nenhum adiantamento encontrado</div>
        ) : (
          <div className="adiantamentos-table-container">
            <table className="adiantamentos-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Colaborador</th>
                  <th>Tipo</th>
                  <th>Valor</th>
                  <th>Descrição</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {adiantamentos.map(adiantamento => (
                  <tr key={adiantamento.id}>
                    <td>{formatarData(adiantamento.data_lancamento)}</td>
                    <td>{adiantamento.colaboradores?.nome || 'N/A'}</td>
                    <td>
                      <span className={`badge ${adiantamento.pgto_tipos?.descricao === 'Adiantamento' ? 'badge-success' : 'badge-warning'}`}>
                        {adiantamento.pgto_tipos?.descricao || 'N/A'}
                      </span>
                    </td>
                    <td className="valor">{formatarMoeda(adiantamento.valor_pago)}</td>
                    <td>{adiantamento.descricao}</td>
                    <td className="actions">
                      {podeEditar() ? (
                        <>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleEdit(adiantamento)}
                          >
                            Editar
                          </button>
                          {podeExcluir() ? (
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteClick(adiantamento.id)}
                            >
                              Excluir
                            </button>
                          ) : (
                            <span className="text-muted small">Apenas administradores podem excluir</span>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">Apenas administradores e administrativos podem editar</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Formulário */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingId ? 'Editar Lançamento' : 'Novo Lançamento'}</h3>
              <button
                className="btn-close"
                onClick={() => {
                  setShowForm(false)
                  setEditingId(null)
                  setFormData({
                    colaborador_id: '',
                    data_lancamento: new Date().toISOString().split('T')[0],
                    valor_pago: '',
                    descricao: '',
                    pgto_id: ''
                  })
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="adiantamento-form">
              <div className="form-group">
                <label>Colaborador *</label>
                <select
                  value={formData.colaborador_id}
                  onChange={(e) => setFormData({...formData, colaborador_id: e.target.value})}
                  required
                >
                  <option value="">Selecione um colaborador</option>
                  {colaboradores.map(colaborador => (
                    <option key={colaborador.id} value={colaborador.id}>
                      {colaborador.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Tipo de Pagamento *</label>
                <select
                  value={formData.pgto_id}
                  onChange={(e) => setFormData({...formData, pgto_id: e.target.value})}
                  required
                >
                  <option value="">Selecione o tipo</option>
                  {pgtoTipos.map(tipo => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.descricao}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Data do Lançamento *</label>
                <input
                  type="date"
                  value={formData.data_lancamento}
                  onChange={(e) => setFormData({...formData, data_lancamento: e.target.value})}
                  required
                />
                {isTipoPagamento() && (
                  <small className="form-text text-muted">
                    A observação será gerada automaticamente baseada na data selecionada.
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Valor Pago *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.valor_pago}
                  onChange={(e) => setFormData({...formData, valor_pago: e.target.value})}
                  placeholder="0,00"
                  required
                />
              </div>

              <div className="form-group">
                <label>Descrição {!isTipoPagamento() ? '*' : ''}</label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                  placeholder={isTipoPagamento() ? "Descrição adicional (opcional)" : "Descreva o motivo do adiantamento"}
                  rows="3"
                  required={!isTipoPagamento()}
                  disabled={isTipoPagamento()}
                />
                {isTipoPagamento() && (
                  <small className="form-text text-muted">
                    A descrição será gerada automaticamente como "Pagamento referente ao [mês da data selecionada]".
                  </small>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowForm(false)
                    setEditingId(null)
                    setFormData({
                      colaborador_id: '',
                      data_lancamento: new Date().toISOString().split('T')[0],
                      valor_pago: '',
                      descricao: '',
                      pgto_id: ''
                    })
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Salvando...' : (editingId ? 'Atualizar' : 'Salvar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-modal">
            <div className="modal-header">
              <h3>⚠️ Confirmar Exclusão</h3>
            </div>
            <div className="modal-body">
              <p>Tem certeza que deseja excluir este lançamento?</p>
              <p className="text-muted">Esta ação não pode ser desfeita.</p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handleCancelDelete}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={loading}
              >
                {loading ? 'Excluindo...' : 'Sim, Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notificações */}
      <div className="notifications-container">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`notification notification-${notification.type}`}
            onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
          >
            {notification.message}
          </div>
        ))}
      </div>
    </div>
  )
}

export default Adiantamento
